import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  calculateDefaultDimensions,
  flattenedDimensionsList,
} from "@/pages/analytics/components/DimensionsSelector/dimensions";
import { calculateDefaultMetrics } from "@/pages/analytics/components/MetricsSelector/metricsList";
import type { AnalyticsState } from "@/types/model/Utilities";
import storageService from "@/services/middleware/storageService";

const detectFormat = (key: string) => {
  let detected: any = {};
  const stored = storageService.getItem(key);
  try {
    detected = typeof stored === "string" ? JSON.parse(stored) : stored || {};
  } catch {
    detected = {};
  }

  if (
    detected &&
    typeof detected === "object" &&
    "dateStart" in detected &&
    "dateEnd" in detected &&
    typeof detected.dateStart === "string" &&
    typeof detected.dateEnd === "string" &&
    (/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{3}Z)?$/.test(
      detected.dateStart
    ) ||
      /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{3}Z)?$/.test(
        detected.dateEnd
      ))
  ) {
    return {
      dateStart: formatYYYYMMDD(new Date(detected.dateStart), "start"),
      dateEnd: formatYYYYMMDD(new Date(detected.dateEnd), "end"),
    };
  }

  return detected;
};
const pad2 = (n: number) => String(n).padStart(2, "0");

type TimeMode = "full" | "start" | "end" | "none";

function formatYYYYMMDD(date: Date, mode: TimeMode = "none"): string {
  const YYYY = date.getFullYear();
  const MM = pad2(date.getMonth() + 1);
  const DD = pad2(date.getDate());
  const datePart = `${YYYY}-${MM}-${DD}`;

  let timePart: string;

  switch (mode) {
    case "full":
      const hh = pad2(date.getHours());
      const mm = pad2(date.getMinutes());
      const ss = pad2(date.getSeconds());
      timePart = `${hh}:${mm}:${ss}`;
      break;
    case "start":
      timePart = "00:00:00";
      break;
    case "end":
      timePart = "23:59:59";
      break;
    default:
      timePart = "";
  }

  return timePart ? `${datePart} ${timePart}` : datePart;
}

const offsetDate = (base: Date, offsetDays: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + offsetDays);

  return d;
};

export const last6daysAndTodayRange = () => {
  const now = new Date();

  const last6daysAndTodayStart = offsetDate(now, -6);
  const last6daysAndTodayEnd = new Date();
  last6daysAndTodayEnd.setHours(23, 59, 59, 999);

  return {
    dateDate: {
      from: last6daysAndTodayStart,
      to: last6daysAndTodayEnd,
      rangeText: "Last 6 days & Today",
    },
    dateString: {
      from: formatYYYYMMDD(last6daysAndTodayStart, "start"),
      to: formatYYYYMMDD(last6daysAndTodayEnd, "end"),
      rangeText: "Last 6 days & Today",
    },
  };
};

const defaultRange = () => {
  const dateStored = detectFormat("intelligence-date");

  if (Object.keys(dateStored)?.length > 0) {
    return {
      dateStart: dateStored.dateStart,
      dateEnd: dateStored.dateEnd,
      rangeText: dateStored.rangeText,
    };
  }
  return {
    dateStart: last6daysAndTodayRange().dateString.from,
    dateEnd: last6daysAndTodayRange().dateString.to,
    rangeText: last6daysAndTodayRange().dateString.rangeText,
  };
};

const defaultFilters: {
  [k: string]: Array<string | number> | string | Date;
} = {
  dateStart: defaultRange().dateStart,
  dateEnd: defaultRange().dateEnd,
  rangeText: defaultRange().rangeText,
  trafficType: [],
  utm_source: [],
  utm_campaign: [],
  utm_medium: [],
  utm_content: [],
  utm_term: [],
  googleAccountId: [],
  googleCampaignId: [],
  googleCampaignType: [],
  googleKeyword: [],
  googlePlacement: [],
  offer: [],
  campaign: [],
  trafficSource: [],
  subPublisher: [],
  device: [],
  os: [],
  browser: [],
  country: [],
  isp: [],
  opt_domain: [],
  referer: [],
  var1: [],
  var2: [],
  var3: [],
  var4: [],
  var5: [],
  var6: [],
  var7: [],
  var8: [],
  var9: [],
  var10: [],
  var11: [],
  var12: [],
  var13: [],
  var14: [],
  var15: [],
  var16: [],
  var17: [],
  var18: [],
  var19: [],
  var20: [],
  hitSecurityLevel: [],
  hitStatus: [],
  trafficStatus: [],
  status: [],
  analysisLevel: [],
};

interface AnalyticsStoreState {
  viewState: AnalyticsState["viewState"];
  setStats: (data: any) => void;
  setErrorStats: (value: boolean) => void;
  metrics: AnalyticsState["metrics"];
  setMetrics: (keys: string[]) => void;
  filters: AnalyticsState["filters"];
  setFilters: (filters: AnalyticsState["filters"]) => void;
  dimensions: AnalyticsState["dimensions"];
  setDimensions: (keys: string[]) => void;
  table: {
    expanded: AnalyticsState["table"]["expanded"];
    columnSorting: AnalyticsState["table"]["columnSorting"];
    page: AnalyticsState["table"]["page"];
    setExpanded: (expanded: AnalyticsState["table"]["expanded"]) => void;
    setCurrentPage: (page: AnalyticsState["table"]["page"]) => void;
    setColumnSorting: (
      sorting: AnalyticsState["table"]["columnSorting"]
    ) => void;
    threats: AnalyticsState["table"]["threats"];
    setExpandedThreats: (threat: string | null, isConversion?: boolean) => void;
    search: AnalyticsState["table"]["search"];
    setSearch: (search: string) => void;
  };
  selectedLabels: AnalyticsState["selectedLabels"];
  setSelectedLabels: (labels: AnalyticsState["selectedLabels"]) => void;
  filtersPromptsHistory: AnalyticsState["filtersPromptsHistory"];
  setFiltersPromptsHistory: (
    filtersPromptsHistory: AnalyticsState["filtersPromptsHistory"]
  ) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const useAnalyticsStore = create<AnalyticsStoreState>()(
  devtools(
    (set) => ({
      viewState: { data: [], currency: "", error: false },
      setStats: (data) => {
        return set((state) => ({
          ...state,
          viewState: { ...data, error: false },
        }));
      },
      setErrorStats: (value) => {
        return set((state) => ({
          ...state,
          viewState: { ...state.viewState, error: value },
        }));
      },
      metrics: calculateDefaultMetrics(),
      setMetrics: (keys) => {
        storageService.setItem("metrics", keys);
        return set((state) => ({
          ...state,
          metrics: {
            keys,
            list: state.metrics.list.filter((e) => keys.indexOf(e.key) > -1),
          },
        }));
      },
      filters: defaultFilters,
      setFilters: (filters) => {
        storageService.setItem(
          "intelligence-date",
          JSON.stringify({
            dateStart: filters.dateStart,
            dateEnd: filters.dateEnd,
            rangeText: filters.rangeText,
          })
        );
        return set((state) => ({
          ...state,
          filters: { ...state.filters, ...filters },
        }));
      },
      dimensions: calculateDefaultDimensions(),
      setDimensions: (keys) => {
        storageService.setItem("dimensions", keys);
        return set((state) => ({
          ...state,
          dimensions: {
            keys,
            list: keys
              .map((dimension: string) =>
                flattenedDimensionsList.find((e) => e.key === dimension)
              )
              .filter(
                (item): item is NonNullable<typeof item> => item !== undefined
              ),
          },
        }));
      },
      selectedLabels: [],
      setSelectedLabels: (labels) => {
        return set((state) => ({
          ...state,
          selectedLabels: labels,
        }));
      },
      table: {
        expanded: {},
        setExpanded: (expanded) => {
          const expandedRows = Object.keys(expanded).length > 0 ? expanded : {};
          return set((state) => ({
            ...state,
            table: {
              ...state.table,
              expanded: expandedRows,
            },
          }));
        },
        columnSorting: {},
        page: 0,
        setCurrentPage: (page) => {
          return set((state) => ({
            ...state,
            table: { ...state.table, page },
          }));
        },
        setColumnSorting: (sorting) => {
          storageService.setItem("column-sort", sorting as unknown as string);
          return set((state) => ({
            ...state,
            table: {
              ...state.table,
              columnSorting: sorting,
            },
          }));
        },
        threats: { rowId: null, isConversion: false },
        setExpandedThreats: (threat, isConversion = false) => {
          return set((state) => ({
            ...state,
            table: {
              ...state.table,
              threats: { rowId: threat, isConversion: isConversion },
            },
          }));
        },
        search: "",
        setSearch: (search) => {
          return set((state) => ({
            ...state,
            table: { ...state.table, search },
          }));
        },
      },
      filtersPromptsHistory: [],
      setFiltersPromptsHistory: (filtersPromptsHistory) => {
        return set((state) => ({
          ...state,
          filtersPromptsHistory,
        }));
      },
      loading: false,
      setLoading: (loading) => set((state) => ({ ...state, loading })),
    }),
    {
      name: "AnalyticsStore",
    }
  )
);
